import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { hrmAPI } from '../../lib/api';

const Settings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState({
    check_in_time: '09:00',
    check_out_time: '18:00',
    late_threshold_minutes: '30',
    working_days_per_month: '26'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await hrmAPI.getSettings();
      if (response.data.success && response.data.data) {
        setSettings(prev => ({
          ...prev,
          ...response.data.data
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      setSaving(true);
      
      const settingsToSave = {
        check_in_time: settings.check_in_time,
        check_out_time: settings.check_out_time,
        late_threshold_minutes: settings.late_threshold_minutes || '30',
        working_days_per_month: settings.working_days_per_month || '26'
      };

      await hrmAPI.saveSettings(settingsToSave);
      setSuccess('Settings saved successfully!');
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Settings</h1>
        <p className="text-gray-600">Manage HRM system settings</p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Attendance Settings</h2>
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
            {success}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Check In Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Check In Time
              </label>
              <input
                type="time"
                name="check_in_time"
                value={settings.check_in_time}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Standard check-in time for employees</p>
            </div>

            {/* Check Out Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Check Out Time
              </label>
              <input
                type="time"
                name="check_out_time"
                value={settings.check_out_time}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Standard check-out time for employees</p>
            </div>

            {/* Late Threshold */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Late Threshold (minutes)
              </label>
              <input
                type="number"
                name="late_threshold_minutes"
                value={settings.late_threshold_minutes}
                onChange={handleChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Minutes after check-in time to mark as late</p>
            </div>

            {/* Working Days Per Month */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Working Days Per Month
              </label>
              <input
                type="number"
                name="working_days_per_month"
                value={settings.working_days_per_month}
                onChange={handleChange}
                min="1"
                max="31"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Number of working days in a month for salary calculation</p>
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/hrm')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-2">💡 Information</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Check In/Check Out times are used for attendance validation</li>
          <li>• Late threshold determines when an employee is marked as late</li>
          <li>• Working days per month is used for salary calculations</li>
        </ul>
      </div>
    </div>
  );
};

export default Settings;
